import { useState } from "react";
import { createCustomer, CreateCustomerData } from "../../services/customers";

interface AddProps {
  onCustomerAdded?: () => void;
  onCancel?: () => void;
}

export const CustomerAdd = ({ onCustomerAdded, onCancel }: AddProps) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateCustomerData>({
    full_name: "",
    email: "",
    phone_country_code: "+1",
    phone_number: "",
    country_of_citizenship: "",
    country_of_residence: "",
    canadian_status: "none",
    service_reason: "express_entry",
    agree_to_terms: false,
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.agree_to_terms) {
      alert("Please agree to the terms and conditions.");
      return;
    }

    try {
      setLoading(true);
      await createCustomer(formData);

      // Reset form
      setFormData({
        full_name: "",
        email: "",
        phone_country_code: "+1",
        phone_number: "",
        country_of_citizenship: "",
        country_of_residence: "",
        canadian_status: "none",
        service_reason: "express_entry",
        agree_to_terms: false,
      });

      onCustomerAdded?.();
    } catch (error) {
      console.error("Error creating customer:", error);
      alert("Failed to create customer. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Add New Customer</h2>
          <p className="text-gray-600 mt-1">
            Fill in the customer information below
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="full_name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Full Name *
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter email address"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="phone_country_code"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Country Code
                </label>
                <select
                  id="phone_country_code"
                  name="phone_country_code"
                  value={formData.phone_country_code}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="+1">+1 (Canada/US)</option>
                  <option value="+44">+44 (UK)</option>
                  <option value="+91">+91 (India)</option>
                  <option value="+977">+977 (Nepal)</option>
                  <option value="+880">+880 (Bangladesh)</option>
                  <option value="+92">+92 (Pakistan)</option>
                  <option value="+61">+61 (Australia)</option>
                  <option value="+86">+86 (China)</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label
                  htmlFor="phone_number"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter phone number"
                />
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Location Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="country_of_citizenship"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Country of Citizenship
                </label>
                <select
                  id="country_of_citizenship"
                  name="country_of_citizenship"
                  value={formData.country_of_citizenship}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select country</option>
                  <option value="CA">Canada</option>
                  <option value="US">United States</option>
                  <option value="IN">India</option>
                  <option value="NP">Nepal</option>
                  <option value="BD">Bangladesh</option>
                  <option value="PK">Pakistan</option>
                  <option value="CN">China</option>
                  <option value="PH">Philippines</option>
                  <option value="NG">Nigeria</option>
                  <option value="GB">United Kingdom</option>
                  <option value="AU">Australia</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="country_of_residence"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Country of Residence
                </label>
                <select
                  id="country_of_residence"
                  name="country_of_residence"
                  value={formData.country_of_residence}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select country</option>
                  <option value="CA">Canada</option>
                  <option value="US">United States</option>
                  <option value="IN">India</option>
                  <option value="NP">Nepal</option>
                  <option value="BD">Bangladesh</option>
                  <option value="PK">Pakistan</option>
                  <option value="CN">China</option>
                  <option value="PH">Philippines</option>
                  <option value="NG">Nigeria</option>
                  <option value="GB">United Kingdom</option>
                  <option value="AU">Australia</option>
                </select>
              </div>
            </div>
          </div>

          {/* Canadian Status and Service */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Canadian Status & Service
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="canadian_status"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Canadian Status
                </label>
                <select
                  id="canadian_status"
                  name="canadian_status"
                  value={formData.canadian_status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="none">None</option>
                  <option value="visitor">Visitor</option>
                  <option value="student">Student</option>
                  <option value="worker">Worker</option>
                  <option value="refugee">Refugee</option>
                  <option value="citizen">Citizen</option>
                  <option value="not_disclose">Not Disclose</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="service_reason"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Service Reason
                </label>
                <select
                  id="service_reason"
                  name="service_reason"
                  value={formData.service_reason}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="express_entry">Express Entry</option>
                  <option value="pnp">Provincial Nominee Program</option>
                  <option value="study_permit">Study Permit</option>
                  <option value="work_permit">Work Permit</option>
                  <option value="family_sponsorship">Family Sponsorship</option>
                  <option value="citizenship">Citizenship</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div>
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="agree_to_terms"
                  name="agree_to_terms"
                  type="checkbox"
                  checked={formData.agree_to_terms}
                  onChange={handleInputChange}
                  required
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label
                  htmlFor="agree_to_terms"
                  className="font-medium text-gray-700"
                >
                  I agree to the terms and conditions *
                </label>
                <p className="text-gray-500">
                  By checking this box, you confirm that you have read and agree
                  to our terms of service and privacy policy.
                </p>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Creating..." : "Create Customer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
